import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import FormInput from '@enerex/unified-ui-react/form/FormInput';

import DeleteRateClassAssociationModal from './DeleteRateClassAssociationModal';
import RateClassAssociationSection from './RateClassAssociationSection';
import {
  deleteSupplierRateClass,
  deleteUtilityRateClass,
  getSupplierRateClasses,
  getUtilityRateClasses,
  updateMasterRateClass,
} from './rateClassApi';
import './RateClassModal.css';

const EditMasterRateClassModal = ({
  show,
  rateClass,
  mode = 'edit',
  refreshKey,
  onSave,
  onCreate,
  onMessage,
  onAddAssociation,
  onHide,
}) => {
  const [utilityRateClasses, setUtilityRateClasses] = useState([]);
  const [supplierRateClasses, setSupplierRateClasses] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, type: null, item: null });
  const [loadingUtility, setLoadingUtility] = useState(false);
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  const [saving, setSaving] = useState(false);
  const { control, reset, watch } = useForm({
    defaultValues: { rateClassId: '', rateClassName: '', rateCode: '', rateClassification: '' },
  });
  const form = watch();
  const isAddMode = mode === 'add';
  const childSectionsDisabled = !rateClass?.rateClassId;

  useEffect(() => {
    if (!show) return;
    if (isAddMode && !rateClass) {
      reset({ rateClassId: '', rateClassName: '', rateCode: '', rateClassification: '' });
      setUtilityRateClasses([]);
      setSupplierRateClasses([]);
      setError('');
      setSuccess('');
      return;
    }
    if (!rateClass) return;
    reset({
      rateClassId: rateClass.rateClassId,
      rateClassName: rateClass.rateClassName || '',
      rateCode: rateClass.rateCode || '',
      rateClassification: rateClass.rateClassification || '',
    });
    setError('');
    setSuccess('');
  }, [show, rateClass, refreshKey, isAddMode, reset]);

  const loadUtilityRateClasses = async (rateClassId) => {
    setLoadingUtility(true);
    try {
      setUtilityRateClasses(await getUtilityRateClasses(rateClassId));
    } catch (loadError) {
      setError(loadError.message || 'Failed to load utility rate classes.');
    } finally {
      setLoadingUtility(false);
    }
  };

  const loadSupplierRateClasses = async (rateClassId) => {
    setLoadingSupplier(true);
    try {
      setSupplierRateClasses(await getSupplierRateClasses(rateClassId));
    } catch (loadError) {
      setError(loadError.message || 'Failed to load supplier rate classes.');
    } finally {
      setLoadingSupplier(false);
    }
  };

  useEffect(() => {
    if (!show || !rateClass?.rateClassId) return;
    loadUtilityRateClasses(rateClass.rateClassId);
    loadSupplierRateClasses(rateClass.rateClassId);
  }, [show, rateClass, refreshKey]);

  useEffect(() => {
    if (!success) return undefined;

    const timeout = window.setTimeout(() => {
      setSuccess('');
    }, 5000);

    return () => window.clearTimeout(timeout);
  }, [success]);

  const utilityRows = useMemo(
    () => utilityRateClasses.filter((item) => item.rateClassId === rateClass?.rateClassId),
    [utilityRateClasses, rateClass]
  );

  const supplierRows = useMemo(
    () => supplierRateClasses.filter((item) => item.rateClassId === rateClass?.rateClassId),
    [supplierRateClasses, rateClass]
  );

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!form.rateClassName.trim()) {
      setError('Please enter rate class.');
      return;
    }

    const rateCode = (form.rateCode || '').trim();
    const rateClassification = (form.rateClassification || '').trim();
    const payload = {
      rateClassName: form.rateClassName.trim(),
      rateCode: rateCode || ' ',
      rateClassification,
    };

    setSaving(true);
    try {
      if (isAddMode && !form.rateClassId) {
        await onCreate(payload);
        onHide();
        return;
      }

      const updatedRateClass = await updateMasterRateClass({
        ...payload,
        rateClassId: form.rateClassId,
      });
      await onSave(updatedRateClass);
      onHide();
    } catch (saveError) {
      setError(saveError.message || 'Failed to save master rate class.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssociation = async () => {
    setError('');
    setSuccess('');
    try {
      if (deleteModal.type === 'utility') {
        await deleteUtilityRateClass(deleteModal.item.utilityRateClassId);
        await loadUtilityRateClasses(rateClass.rateClassId);
        setSuccess('Utility Rate Class deleted successfully.');
        onMessage(null);
      } else {
        await deleteSupplierRateClass(deleteModal.item.supplierRateClassId);
        await loadSupplierRateClasses(rateClass.rateClassId);
        setSuccess('Supplier Rate Class deleted successfully.');
        onMessage(null);
      }
      setDeleteModal({ show: false, type: null, item: null });
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete rate class.');
    }
  };

  return (
    <>
      <ConfirmationModal
        centered
        dialogClassName={isAddMode ? 'modal-md' : 'modal-xl rate-class-edit-modal'}
        show={show}
        onHide={onHide}
        title={isAddMode ? 'Add Rate Class' : 'Edit Rate Class'}
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={saving}
        onConfirm={handleSave}
      >
        {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}
        {success ? (
          <div className="alert alert-success alert-dismissible" role="alert">
            {success}
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setSuccess('')} />
          </div>
        ) : null}

        <div className="row g-3">
          <div className={isAddMode ? 'col-12' : 'col-md-4'}>
            <FormInput type="text" name="rateClassName" control={control} label="Rate Class" />
          </div>
          <div className={isAddMode ? 'col-12' : 'col-md-4'}>
            <FormInput type="text" name="rateCode" control={control} label="Rate Code" />
          </div>
          <div className={isAddMode ? 'col-12' : 'col-md-4'}>
            <FormInput type="text" name="rateClassification" control={control} label="Rate Classification" />
          </div>
        </div>

        {!isAddMode ? (
          <>
            <RateClassAssociationSection
              title="Utility Rate Classes"
              addLabel="Add Utility Rate Class"
              rows={utilityRows}
              loading={loadingUtility}
              nameHeader="Utility"
              emptyText="No Data found"
              showAliasColumn={false}
              scrollable
              disabled={childSectionsDisabled}
              onAdd={() => onAddAssociation('utility')}
              onDelete={(item) => setDeleteModal({ show: true, type: 'utility', item })}
            />

            <RateClassAssociationSection
              title="Supplier Rate Classes Aliases"
              addLabel="Add Supplier Rate Class"
              rows={supplierRows}
              loading={loadingSupplier}
              nameHeader="Utility"
              aliasHeader="Rate Class Alias"
              supplierHeader="Supplier"
              emptyText="No Data found"
              showAliasColumn
              showSupplierColumn
              scrollable
              disabled={childSectionsDisabled}
              onAdd={() => onAddAssociation('supplier', utilityRows)}
              onDelete={(item) => setDeleteModal({ show: true, type: 'supplier', item })}
            />
          </>
        ) : null}
      </ConfirmationModal>

      <DeleteRateClassAssociationModal show={deleteModal.show} type={deleteModal.type} item={deleteModal.item} onCancel={() => setDeleteModal({ show: false, type: null, item: null })} onConfirm={handleDeleteAssociation} />
    </>
  );
};

export default EditMasterRateClassModal;
